import { PaySmallSmallActions } from '@/store/pay-small-small-reducer'
import { RootState } from '@/store'
import { ToolkitStore } from '@reduxjs/toolkit/dist/configureStore'
import TransactionService from './transaction-service'
import { AxiosError } from 'axios'
import { delayAsyncCall } from '@/utils'
import { PSS_CONFIG_FALLBACK } from '@/constants'
import { PaySmallSmallCheckoutType } from '@/constants/transactions'
import {
    UserProfileStatus,
    PaySmallSmallInstallment,
    PaySmallSmallPayment,
    PaySmallSmallCheckoutConfig,
    CreditAllocationInfo,
} from '@/types/transactions'

export type CreditLimits = {
    dailyLimit: number
    weeklyLimit: number
    monthlyLimit: number
    remainingLimit: number
}

export enum PaySmallSmallPaymentFlowType {
    IS_ELIGIBLE = 'IS_ELIGIBLE',
    IS_INELIGIBLE = 'IS_INELIGIBLE',
    IS_UNQUALIFIED = 'IS_UNQUALIFIED',
    HAS_EXCEEDED_DAILY_LIMIT = 'HAS_EXCEEDED_DAILY_LIMIT',
    HAS_OVERDUE_DEBT = 'HAS_OVERDUE_DEBT',
    HAS_DEFAULTED_PAYMENTS = 'HAS_DEFAULTED_PAYMENTS',
    BLACKLISTED = 'BLACKLISTED',
    HAS_FAILED_CHECKS = 'HAS_FAILED_CHECKS',
}

export interface ManualRepaymentData {
    amountToPay: number
    checkoutType: PaySmallSmallCheckoutType | string
    installmentId?: string
    transactionId?: string
}

export interface EligibilityFailurePayload {
    type: PaySmallSmallPaymentFlowType
    missedPayments?: PaySmallSmallPayment[]
    failedChecksData?: {
        reason: string
        title: string
        content: string
    }
}

export default class PaySmallSmallService {
    constructor(
        private store: ToolkitStore<RootState>,
        private transactionService: TransactionService,
        private paySmallSmallActions: PaySmallSmallActions,
    ) {}

    borrower = this.store.getState().paySmallSmall.borrower

    profileStatus: UserProfileStatus | null = null

    borrowerStatus: { hasPassedChecks: boolean; reason: string } | null = null

    allocationData: CreditAllocationInfo | null = null

    creditLimits = {
        dailyLimit: 0,
        weeklyLimit: 0,
        monthlyLimit: 0,
        remainingLimit: 0,
    } as CreditLimits

    isGhanaCardActivationPending: boolean = true
    /**
     * Updates the credit limits for the pay small small service.
     * This method calculates the daily, weekly, and monthly limits,
     * as well as the remaining limit, and dispatches an action to update the state.
     */
    async updateCreditLimits(allocationData: CreditAllocationInfo | null = null) {
        if (allocationData && allocationData.tier <= 1) {
            await this.transactionService.getPaySmallSmallCreditConfig()
        }

        this.allocationData = allocationData

        const monthlyLimit = allocationData?.creditAllocation?.borrowableAmount ?? 0,
            dailyLimit = allocationData?.creditAllocation?.borrowableAmountPerDay ?? 0,
            weeklyLimit = allocationData?.creditAllocation?.borrowableAmountPerWeek ?? 0,
            remainingLimit = this.getRemainingDailyLimit()

        const creditLimits = {
            dailyLimit: dailyLimit,
            weeklyLimit: weeklyLimit,
            monthlyLimit: monthlyLimit,
            remainingLimit: remainingLimit,
        }

        this.creditLimits = creditLimits

        this.store.dispatch(
            this.paySmallSmallActions.setState({
                creditLimits,
            }),
        )
    }

    /**
     * Checks if the user has exhausted their credit limit.
     * @returns {boolean} True if the user has exhausted their credit limit, false otherwise.
     */
    userExhaustedCreditLimit(): boolean {
        return this.getRemainingDailyLimit() <= 0
    }

    /**
     * Checks if the user has exceeded the daily limit based on the specified amount.
     * @param amount - The amount to check against the daily limit.
     * @returns A boolean value indicating whether the user has exceeded the daily limit.
     */
    userHasExceededDailyLimit(amount: number): boolean {
        const allocationData = this.store.getState().paySmallSmall.creditAllocationData
        if (!allocationData) {
            return true
        }

        return amount > this.getRemainingDailyLimit()
    }

    /**
     * The amount to pay if the user exceeds the remaining limit.
     * It is calculated by multiplying the remaining limit by 1.25.
     */
    amountToPayIfUserExceedsRemainingLimit = () => {
        const allocationData = this.store.getState().paySmallSmall.creditAllocationData

        const oneThirdOfRemainingLimit = this.getRemainingDailyLimit() / 3
        const tier1Amount = oneThirdOfRemainingLimit + this.getRemainingDailyLimit()
        const tier2Amount = this.getRemainingDailyLimit()

        if (allocationData?.tier === 1) {
            return tier1Amount
        } else {
            return tier2Amount
        }
    }

    /**
     * Calculates the remaining credit limit for the borrower.
     * @returns The remaining credit limit.
     */
    getRemainingDailyLimit(): number {
        const allocationData = this.allocationData
        if (!allocationData) {
            return 0
        }

        const limit = allocationData.creditAllocation.borrowableAmountPerDay - allocationData.totalCreditTakenToday
        return limit <= 0 ? 0 : limit
    }

    getInstalmentsCount(): number {
        const allocationData = this.store.getState().paySmallSmall.creditAllocationData
        if (!allocationData) {
            return 0
        }
        const numOfInstallment = allocationData?.creditAllocation?.installmentCount ?? 4
        return numOfInstallment
    }

    /**
     * Calculates the payment plan for a given amount in a 4-installment pay small small plan.
     * @param amount - The total amount to be paid.
     * @returns An array of partial pay small small installments.
     */
    calculateInstallments(amount: number): Partial<PaySmallSmallInstallment>[] {
        const allocationData = this.store.getState().paySmallSmall.creditAllocationData

        this.allocationData = allocationData

        let numOfInstallment = allocationData?.creditAllocation?.installmentCount ?? 4
        const paybackFrequency = allocationData?.creditAllocation?.paybackFrequency ?? 4
        const totalRepayment = this.calculateAmountToPay(amount)

        const totalInterest = totalRepayment - amount

        const startingDate = new Date(allocationData?.systemTime ?? new Date().toISOString())

        const paymentPlan: Partial<PaySmallSmallInstallment>[] = []

        if ((allocationData?.tier ?? 1) < 2) {
            for (let count = 0; count < numOfInstallment; count++) {
                const paymentDate: Date = new Date(
                    startingDate.getTime() + paybackFrequency * 24 * 60 * 60 * 1000 * count,
                ) // starting time + paybackFrequency
                const paymentAmount = amount / numOfInstallment
                const amountToPay = paymentAmount
                paymentPlan.push({
                    repaymentDate: paymentDate,
                    amount: amountToPay,
                    status: 'unpaid',
                })
            }

            if (paymentPlan.length > 0) {
                const firstPayment = paymentPlan[0]
                firstPayment.amount! += totalInterest
                firstPayment.interestAmount = totalInterest
                firstPayment.status = 'paid'
            }
        } else {
            numOfInstallment += 1

            for (let count = 0; count < numOfInstallment; count++) {
                const paymentDate: Date = new Date(
                    startingDate.getTime() + paybackFrequency * 24 * 60 * 60 * 1000 * count,
                ) // starting time + paybackFrequency

                if (count === 0) {
                    paymentPlan.push({
                        repaymentDate: paymentDate,
                        amount: 0,
                        status: 'paid',
                    })
                    continue
                }

                const paymentAmount = totalRepayment / (numOfInstallment - 1)
                const amountToPay = paymentAmount
                paymentPlan.push({
                    repaymentDate: paymentDate,
                    amount: amountToPay,
                    status: 'unpaid',
                })
            }
        }

        return paymentPlan
    }

    /**
     * Calculates the total amount to be paid, including interest, for a given amount.
     *
     * @param amount - The principal amount to be paid.
     * @returns The total amount to be paid, rounded to two decimal places.
     */
    calculateAmountToPay(amount: number): number {
        const totalRepayment = amount + this.interestOnAmount(amount)
        const value = Math.round(totalRepayment * 100) / 100
        return value
    }

    /**
     * Calculates the interest on a given amount.
     * @param amount - The amount to calculate the interest on.
     * @returns The calculated interest value.
     */
    interestOnAmount(amount: number): number {
        const allocationData = this.store.getState().paySmallSmall.creditAllocationData
        if (!allocationData) {
            return 0
        }

        const tier = allocationData?.tier ?? 1

        const interestRate = allocationData?.creditAllocation?.interestRate ?? 0
        const interestOnAmount = interestRate * amount

        if (tier < 2) {
            const config = this.store.getState().paySmallSmall.config
            if (!config) {
                return 0
            }

            return interestOnAmount * config.interestComputationPercentage
        }

        return interestOnAmount
    }

    private shouldExitEarly = (): boolean => {
        return this.shouldExit ?? false
    }

    private shouldExit: boolean = false

    /**
     * Checks the user's eligibility for credit based on various conditions.
     *
     * @param {Object} options - The options for the checkUserHistoryElegibility method.
     * @param {Function} options.onSuccess - The callback function to be called when the user is eligible for credit.
     * @param {Function} options.onFailure - The callback function to be called when the user is not eligible for credit.
     * @param {Function} options.onStatusChange - The callback function to be called when the status changes during the eligibility check.
     * @param {boolean} options.isMakingPayment - Indicates whether the user is making a payment.
     * @param {number} options.amountToPay - The amount to be paid by the user.
     * @returns {Promise<any>} - A promise that resolves when the eligibility check is complete.
     */
    checkUserEligibility = async ({
        onSuccess,
        onFailure,
        onStatusChange,
        isMakingPayment,
        amountToPay,
        origin = 'profile',
    }: {
        onSuccess?: () => void
        onFailure: (payload: EligibilityFailurePayload) => void
        onStatusChange?: (status: string) => void
        isMakingPayment?: boolean
        amountToPay?: number
        origin?: 'profile' | 'payment'
    }) => {
        try {
            // Check payment channel availability
            const isChannelAvailable = await this.checkPaymentChannelAvailability()
            if (!isChannelAvailable) {
                onFailure({
                    type: PaySmallSmallPaymentFlowType.IS_INELIGIBLE,
                })
                return
            }

            // Get and validate profile status
            await this.fetchAndValidateProfileStatus(origin, onFailure)
            if (this.shouldExitEarly()) return

            // Check credit score qualification
            const isQualified = await this.checkCreditScoreQualification()
            if (!isQualified) {
                onFailure({
                    type: PaySmallSmallPaymentFlowType.IS_INELIGIBLE,
                })
                return
            }

            // Update credit limits
            await this.fetchAndUpdateCreditLimits(origin)

            // Check credit default history
            const hasCreditDefaultHistory = await this.checkCreditDefaultHistory({
                onStatusChange,
                onFailure,
            })

            // Handle payment-specific validations
            if (isMakingPayment || amountToPay) {
                if (hasCreditDefaultHistory) return

                const canProceedWithPayment = await this.validatePaymentEligibility({
                    onStatusChange,
                    onFailure,
                    amountToPay,
                    origin,
                })
                if (!canProceedWithPayment) return
            }

            onSuccess?.()
        } catch (error: any) {
            throw error
        }
    }

    private checkPaymentChannelAvailability = async (): Promise<boolean> => {
        const statusRes = await this.transactionService.getPaySmallSmallPaymentChannelStatus()
        return statusRes.isPaymentOptionAvailable
    }

    private fetchAndValidateProfileStatus = async (
        origin: 'profile' | 'payment',
        onFailure: (payload: EligibilityFailurePayload) => void,
    ) => {
        const profileStatusRes = await this.transactionService.getPaySmallSmallStatus()
        this.profileStatus = profileStatusRes
        this.isGhanaCardActivationPending = profileStatusRes.isGhanaCardActivationPending

        if (origin === 'payment') {
            await this.validateBorrowerStatus(onFailure)
        }

        this.validateProfileRequirements(profileStatusRes, onFailure)
    }

    private validateBorrowerStatus = async (onFailure: (payload: EligibilityFailurePayload) => void) => {
        const borrowerStatus = await this.transactionService.getBorrowerVerificationStatus()
        this.borrowerStatus = borrowerStatus

        if (!borrowerStatus.hasPassedChecks) {
            onFailure({
                type: PaySmallSmallPaymentFlowType.HAS_FAILED_CHECKS,
                failedChecksData: {
                    reason: borrowerStatus.reason,
                    title: borrowerStatus.title,
                    content: borrowerStatus.content,
                },
            })
            this.shouldExit = true
        }
    }

    private validateProfileRequirements = (
        profileStatus: any,
        onFailure: (type: EligibilityFailurePayload) => void,
    ) => {
        if (profileStatus.isBlacklisted) {
            onFailure({ type: PaySmallSmallPaymentFlowType.BLACKLISTED })
            this.shouldExit = true
            return
        }

        const hasRequiredVerification =
            profileStatus.isGhanaCardVerified &&
            profileStatus.hasActiveProfilePicture &&
            !profileStatus.isGhanaCardActivationPending

        if (!hasRequiredVerification) {
            onFailure({ type: PaySmallSmallPaymentFlowType.IS_INELIGIBLE })
            this.shouldExit = true
        }
    }

    private checkCreditScoreQualification = async (): Promise<boolean> => {
        const scoreRes = await this.transactionService.getPaySmallSmallCreditScore()
        return scoreRes?.isQualified ?? false
    }

    private fetchAndUpdateCreditLimits = async (origin: 'profile' | 'payment') => {
        const allocationData =
            origin === 'profile'
                ? await this.transactionService.getPssExistingCreditAllocationInfo()
                : await this.transactionService.getOutstandingCreditAllocationInfo()

        await this.updateCreditLimits(allocationData)
    }

    private validatePaymentEligibility = async ({
        onStatusChange,
        onFailure,
        amountToPay,
        origin,
    }: {
        onStatusChange?: (status: string) => void
        onFailure: (payload: EligibilityFailurePayload) => void
        amountToPay?: number
        origin: 'profile' | 'payment'
    }): Promise<boolean> => {
        onStatusChange?.('Calculating your credit limit')

        // Validate monthly limit
        if ((this.creditLimits?.monthlyLimit ?? 0) <= 0) {
            onFailure({
                type: PaySmallSmallPaymentFlowType.IS_UNQUALIFIED,
            })
            return false
        }

        // Check daily limit for payment flow
        if (origin === 'payment' && this.userHasExceededDailyLimit(amountToPay!)) {
            onFailure({
                type: PaySmallSmallPaymentFlowType.HAS_EXCEEDED_DAILY_LIMIT,
            })
            return false
        }

        return true
    }

    async checkCreditDefaultHistory({
        onStatusChange,
        onFailure,
    }: {
        onStatusChange?: (status: string) => void
        onFailure: (payload: EligibilityFailurePayload) => void
    }) {
        onStatusChange?.('Checking past credit default history')
        const borrowerData = await this.transactionService.getPaySmallSmallBorrowerData()

        if (borrowerData?.repayments && borrowerData?.repayments.totalOverdueAmount > 0) {
            onStatusChange?.('Checking unpaid and overdue withdrawals')
            const res = await this.transactionService.getOverduePayments()

            await delayAsyncCall({
                funcToBeCalled: async () => {},
                delay: 850,
            })

            onStatusChange?.('Calculating your credit limit')
            if (res.length > 0) {
                onFailure({
                    type: PaySmallSmallPaymentFlowType.HAS_DEFAULTED_PAYMENTS,
                    missedPayments: res,
                })
                return true
            }

            if ((this.creditLimits?.monthlyLimit ?? 0) <= 0 && res.length <= 0) {
                onFailure({
                    type: PaySmallSmallPaymentFlowType.IS_UNQUALIFIED,
                })
                return true
            }
        }

        return false
    }

    /**
     * Handles the beginning of a manual repayment process.
     *
     * @param manualRepaymentData - The data required for the manual repayment.
     * @param onSuccess - The callback function to be called on successful repayment.
     * @param onError - The callback function to be called on error.
     */
    beginManualRepayment = async ({
        manualRepaymentData,
        onSuccess,
        onError,
    }: {
        manualRepaymentData: ManualRepaymentData
        onError: (error: AxiosError) => void
        onSuccess: (response: PaySmallSmallCheckoutConfig) => void
    }) => {
        try {
            const res = await this.transactionService.getPaySmallSmallCheckoutConfig({
                checkoutType: manualRepaymentData.checkoutType,
                transactionId: manualRepaymentData.transactionId,
                installmentId: manualRepaymentData.installmentId,
            })
            onSuccess(res)
        } catch (error) {
            onError(error as AxiosError)
        }
    }

    /**
     * Calculates the late payment fees for a defaulted payment.
     * @param {Date | string} dateOfDefault - The defaulted payment object.
     * @param {number} amount - The amount of the defaulted payment.
     * @param {number} latePaymentFeePercentage - The date of default for the payment.
     * @returns {number} - The calculated late payment fees.
     */
    getLatePaymentFees = ({
        dateOfDefault,
        latePaymentFeePercentage,
        amount,
    }: {
        dateOfDefault: Date | string
        latePaymentFeePercentage: number
        amount: number
    }): number => {
        const config = this.store.getState().paySmallSmall.creditAllocationData ?? PSS_CONFIG_FALLBACK

        const getSystemTime = new Date(config.systemTime)
        const defaultDate = new Date(dateOfDefault)
        const diffInMs = getSystemTime.getTime() - defaultDate.getTime()
        const numberOfDaysDefaulted = Math.max(0, Math.floor(diffInMs / (1000 * 60 * 60 * 24)))

        const latePaymentFeePercentageFee = latePaymentFeePercentage
        const amountToPay = (latePaymentFeePercentageFee / 100) * amount * numberOfDaysDefaulted

        return amountToPay < 0 ? 0 : amountToPay
    }

    /**
     * Calculates the total amount of defaulted payments.
     *
     * @param defaultedPayments - An array of defaulted payments.
     * @returns The total amount of defaulted payments.
     */
    totalAmountDefaulted(defaultedPayments: PaySmallSmallPayment[]): number {
        if (defaultedPayments?.length > 0) {
            return defaultedPayments
                .map((e: PaySmallSmallPayment) => e.amount)
                .reduce((value: number, element: number) => (value ?? 0) + (element ?? 0), 0)
        } else {
            return 0
        }
    }

    /**
     * Calculates the sum of all late payment fees for a given array of defaulted payments.
     *
     * @param defaultedPayments - An array of defaulted payments.
     * @returns The sum of all late payment fees.
     */
    sumOfAllLatePaymentFees(defaultedPayments: PaySmallSmallPayment[]): number {
        if (defaultedPayments?.length > 0) {
            let sum: number = 0
            for (const data of defaultedPayments ?? []) {
                sum += this.getLatePaymentFees({
                    dateOfDefault: data.dateOfDefault,
                    latePaymentFeePercentage: data.latePaymentFeePercentage,
                    amount: data.amount,
                })
            }

            return sum
        } else {
            return 0
        }
    }
}
